import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { SOLUTIONS } from "@/data/content";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { Reveal } from "@/components/site/Reveal";
import { useSeo } from "@/hooks/useSeo";

const Detail = ({ s, i }) => {
  const flip = i % 2 === 1;
  return (
    <section id={s.id} data-testid={`solution-detail-${s.id}`} className={`scroll-mt-24 py-20 sm:py-28 ${i % 2 ? "bg-[#EAF0F7]" : "bg-[#FAFCFF]"}`}>
      <div className="container-x grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
          <div className={`relative aspect-[4/3] overflow-hidden rounded-3xl bg-[#14306B] ${flip ? "clip-notch" : "clip-frame"}`}>
            <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E2657]/60 to-transparent" />
            <span className="absolute bottom-5 left-5 font-mono text-xs uppercase tracking-[0.2em] text-cyan-300">0{i + 1} / 0{SOLUTIONS.length}</span>
          </div>
        </Reveal>
        <div className="lg:col-span-6">
          <Reveal>
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#0E2657] text-cyan-300"><s.icon className="h-5 w-5" /></span>
            <p className="eyebrow mt-6 text-[#1777AF]">{s.kicker}</p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{s.title}</h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg">{s.blurb}</p>
          </Reveal>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {s.features.map((f, k) => (
              <motion.li
                key={f}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ delay: k * 0.06, duration: 0.6 }}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700"
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1777AF]" /> {f}
              </motion.li>
            ))}
          </ul>
          <Link to={`/contact?need=${s.id}`} data-testid={`solution-cta-${s.id}`} className="btn-primary mt-8">
            Talk to a specialist <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function Solutions() {
  const { hash } = useLocation();
  useSeo("Solutions | Bodhatek", "Affordable managed IT, managed dispatch and nationwide onsite support across the USA.");

  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 120);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="solutions-page">
      <PageHero
        testId="solutions-hero"
        eyebrow="Solutions"
        lines={["Everything that keeps", { text: "your business moving.", className: "text-grad" }]}
        sub="Three simple services, one affordable team. Pick what you need — or let us handle it all, anywhere in the USA."
      />
      <nav aria-label="Solutions" className="sticky top-[84px] z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="container-x flex gap-2 overflow-x-auto py-3">
          {SOLUTIONS.map((s) => (
            <Link key={s.id} to={`#${s.id}`} data-testid={`solutions-jump-${s.id}`} className="whitespace-nowrap rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-[#1777AF] hover:text-[#1777AF]">
              {s.title}
            </Link>
          ))}
        </div>
      </nav>
      {SOLUTIONS.map((s, i) => <Detail key={s.id} s={s} i={i} />)}
      <CTABand />
    </div>
  );
}
